import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Loader2, PackageSearch, AlertTriangle, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

export default function AdminDiagLogistica() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [filterEstado, setFilterEstado] = useState("__all__");
  const [filterCliente, setFilterCliente] = useState("__all__");
  const [soloAnomalias, setSoloAnomalias] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/diag/componentes-logistica");
      setData(data);
      toast.message(`${data.total} componentes en flujo activo · ${data.anomalias} con anomalías`);
    } catch (e) {
      toast.error("Error: " + (e?.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const descargarCSV = async () => {
    try {
      const r = await api.get("/admin/diag/componentes-logistica?formato=csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "diag_componentes_logistica.csv"; a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch (e) { toast.error("Error: " + e.message); }
  };

  const estadosUnicos = useMemo(() => Array.from(new Set((data?.rows || []).map((r) => r.estado))).sort(), [data]);
  const clientesUnicos = useMemo(() => Array.from(new Set((data?.rows || []).map((r) => r.cliente).filter(Boolean))).sort(), [data]);

  const rows = useMemo(() => {
    if (!data) return [];
    return data.rows.filter((r) => {
      if (filterEstado !== "__all__" && r.estado !== filterEstado) return false;
      if (filterCliente !== "__all__" && r.cliente !== filterCliente) return false;
      if (soloAnomalias && !r.anomalias) return false;
      if (search) {
        const t = `${r.peticion_codigo || ""} ${r.peticion_nombre || ""} ${r.comp_nombre || ""} ${r.cliente || ""} ${r.espacio || ""}`.toLowerCase();
        if (!t.includes(search.toLowerCase())) return false;
      }
      return true;
    });
  }, [data, search, filterEstado, filterCliente, soloAnomalias]);

  return (
    <div className="space-y-4" data-testid="page-diag-logistica">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="bg-blue-500/15 border border-blue-500/30 rounded-lg p-2.5">
            <PackageSearch className="w-6 h-6 text-blue-300" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-zinc-100">Diagnóstico Logística — Componentes "perdidos"</h1>
            <p className="text-sm text-zinc-400 mt-0.5">
              Lista TODOS los componentes en flujo activo con la pantalla donde deberían aparecer.
              Útil cuando duplicas peticiones y "no encuentras" alguna en Recepción.
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={load} disabled={loading} variant="outline"
            className="bg-transparent border-zinc-700" data-testid="btn-reload">
            {loading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
            Recargar
          </Button>
          <Button onClick={descargarCSV} className="bg-blue-500 hover:bg-blue-400 text-white"
            data-testid="btn-descargar-csv">
            <Download className="w-4 h-4 mr-1" /> CSV
          </Button>
        </div>
      </div>

      {data && (
        <Card className="bg-zinc-900/60 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-zinc-300 flex items-center gap-2 flex-wrap">
              <Badge className="bg-zinc-800 text-zinc-200">{data.total} componentes activos</Badge>
              {data.anomalias > 0 && (
                <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                  <AlertTriangle className="w-3 h-3 mr-1" /> {data.anomalias} anomalías
                </Badge>
              )}
              <span className="text-zinc-500 ml-auto">{rows.length} filtrados</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2 mb-3">
              <Input placeholder="Buscar petición, cliente, espacio…" value={search}
                data-testid="search"
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-64 bg-zinc-950 border-zinc-800 text-xs" />
              <Select value={filterEstado} onValueChange={setFilterEstado}>
                <SelectTrigger data-testid="filter-estado"
                  className="h-8 w-44 bg-zinc-950 border-zinc-800 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="__all__">Todos los estados</SelectItem>
                  {estadosUnicos.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterCliente} onValueChange={setFilterCliente}>
                <SelectTrigger data-testid="filter-cliente"
                  className="h-8 w-48 bg-zinc-950 border-zinc-800 text-xs"><SelectValue placeholder="Cliente" /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                  <SelectItem value="__all__">Todos los clientes</SelectItem>
                  {clientesUnicos.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <label className="flex items-center gap-1.5 text-xs text-zinc-300 px-2">
                <input type="checkbox" checked={soloAnomalias} onChange={(e) => setSoloAnomalias(e.target.checked)}
                  data-testid="filter-anomalias"
                  className="accent-red-400" />
                Solo anomalías
              </label>
            </div>
            <div className="overflow-x-auto max-h-[70vh]">
              <table className="w-full text-xs">
                <thead className="text-zinc-500 text-[10px] uppercase border-b border-zinc-800 sticky top-0 bg-zinc-900">
                  <tr>
                    <th className="text-left py-2 px-2">Petición</th>
                    <th className="text-left py-2 px-2">Cliente</th>
                    <th className="text-left py-2 px-2">Componente</th>
                    <th className="text-left py-2 px-2">Estado</th>
                    <th className="text-left py-2 px-2">Origen</th>
                    <th className="text-left py-2 px-2">Espacio</th>
                    <th className="text-left py-2 px-2">Pantalla esperada</th>
                    <th className="text-left py-2 px-2">⚠ Anomalías</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.comp_id}
                      className={`border-b border-zinc-900 hover:bg-zinc-900/40 ${r.anomalias ? "bg-red-500/[0.03]" : ""}`}>
                      <td className="py-1.5 px-2 text-amber-300 whitespace-nowrap">
                        <Link to={`/peticiones?codigo=${r.peticion_codigo}`} target="_blank"
                          className="hover:underline inline-flex items-center gap-0.5">
                          {r.peticion_codigo || "—"} <ExternalLink className="w-2.5 h-2.5" />
                        </Link>
                        <div className="text-[10px] text-zinc-600">{r.peticion_estado}</div>
                      </td>
                      <td className="py-1.5 px-2 text-zinc-400">{r.cliente || "—"}</td>
                      <td className="py-1.5 px-2 text-zinc-200">{r.comp_nombre}</td>
                      <td className="py-1.5 px-2">
                        <Badge className="bg-zinc-800 text-zinc-200 text-[10px]">{r.estado}</Badge>
                      </td>
                      <td className="py-1.5 px-2 text-zinc-400 text-[11px]">{r.origen}</td>
                      <td className="py-1.5 px-2 text-zinc-400">{r.espacio || "—"}<div className="text-[10px] text-zinc-600">{r.ciudad}</div></td>
                      <td className="py-1.5 px-2 text-violet-300">{r.pantalla_esperada}</td>
                      <td className="py-1.5 px-2 text-red-300 text-[11px]">{r.anomalias || ""}</td>
                    </tr>
                  ))}
                  {rows.length === 0 && (
                    <tr><td colSpan={8} className="py-8 text-center text-zinc-500">Sin resultados con los filtros aplicados.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
